import { useState, useMemo, useEffect, useCallback } from 'react';
import { debounce } from 'lodash-es';
import { TextInput, Checkbox } from '@carbon/react';
import type {
  MultiSelectTextSearchComboFilterField,
  FacetBucket,
  FilterTemplateProps,
} from '../../types';
import {
  useModalFacets,
  useCategoryFields,
  useHiddenWhileEmpty,
} from '../template-hooks';
import { toggleAllVisible } from '../select-all';
import { BucketCheckboxList } from '../bucket-checkbox-list';
import { PasteListControl } from 'components/paste-list';
import { useIsFilterRail } from '../context';
import { FieldLabel } from '../field-label';
import { FacetMultiSelect } from '../facet-multi-select';
import style from '../filter-modal.module.scss';

const FACET_SEARCH_DEBOUNCE_MS = 300;

interface ComboGroupProps {
  field: MultiSelectTextSearchComboFilterField;
  formValues: Record<string, unknown>;
  facetBuckets: FacetBucket[];
  onFormChange: (key: string, value: unknown) => void;
}

/**
 * One typeahead party filter: a server-searched name box (optionally applied
 * as a text search) and the matching names, as a floating dropdown in a
 * compact form, else as Select All and checkboxes.
 * @param {ComboGroupProps} props The filter.
 * @return {JSX.Element | null} The filter.
 */
function ComboGroup({
  field,
  formValues,
  facetBuckets,
  onFormChange,
}: ComboGroupProps) {
  const rail = useIsFilterRail();
  const selectedIds = useMemo(
    () => (formValues[field.param] as string[] | undefined) ?? [],
    [formValues, field.param]
  );
  const [searchText, setSearchText] = useState(
    () => (formValues[field.textParam] as string) ?? ''
  );

  const facetSearchKey = `facet_search_${field.id}`;

  const textApplied = Boolean(formValues[field.textParam]);

  // The chosen names, kept while a search lists other names in their place.
  const [chosen, setChosen] = useState<FacetBucket[]>(() =>
    facetBuckets.filter((b) => selectedIds.includes(b.id))
  );

  const debouncedFacetSearch = useMemo(
    () =>
      debounce((value: string) => {
        onFormChange(facetSearchKey, value || undefined);
      }, FACET_SEARCH_DEBOUNCE_MS),
    [onFormChange, facetSearchKey]
  );

  useEffect(() => {
    return () => debouncedFacetSearch.cancel();
  }, [debouncedFacetSearch]);

  const handleInputChange = useCallback(
    (value: string) => {
      setSearchText(value);
      onFormChange(field.textParam, undefined);
      debouncedFacetSearch(value);
    },
    [onFormChange, field.textParam, debouncedFacetSearch]
  );

  const handleApplyTextToggle = useCallback(
    (checked: boolean) => {
      onFormChange(
        field.textParam,
        checked ? searchText || undefined : undefined
      );
    },
    [onFormChange, field.textParam, searchText]
  );

  const allVisibleSelected = useMemo(
    () =>
      facetBuckets.length > 0 &&
      facetBuckets.every((b) => selectedIds.includes(b.id)),
    [facetBuckets, selectedIds]
  );

  const handleToggleAll = useCallback(() => {
    const next = toggleAllVisible(
      selectedIds,
      facetBuckets.map((b) => b.id),
      allVisibleSelected
    );
    onFormChange(field.param, next.length > 0 ? next : undefined);
  }, [
    allVisibleSelected,
    facetBuckets,
    selectedIds,
    onFormChange,
    field.param,
  ]);

  const handleCheckboxChange = useCallback(
    (id: string, checked: boolean) => {
      const next = checked
        ? [...selectedIds, id]
        : selectedIds.filter((v) => v !== id);
      onFormChange(field.param, next.length > 0 ? next : undefined);
    },
    [selectedIds, onFormChange, field.param]
  );

  const hidden = useHiddenWhileEmpty(
    facetBuckets.length > 0,
    selectedIds.length > 0 || Boolean(searchText)
  );
  if (hidden) return null;

  const applyText = searchText && (
    <Checkbox
      id={`combo-apply-text-${field.id}`}
      labelText={`Apply "${searchText}" as a text search`}
      checked={textApplied}
      onChange={(_evt, { checked }: { checked: boolean }) =>
        handleApplyTextToggle(checked)
      }
    />
  );

  if (rail) {
    const options = [
      ...chosen.filter(
        (b) =>
          selectedIds.includes(b.id) && !facetBuckets.some((o) => o.id === b.id)
      ),
      ...facetBuckets,
    ];
    return (
      <div className={style.comboGroup}>
        {field.helperText && (
          <div className={style.fieldLabelRow}>
            <FieldLabel field={field} />
          </div>
        )}
        <FacetMultiSelect
          id={field.id}
          titleText={field.label}
          hideLabel={!!field.helperText}
          placeholder={
            field.placeholder ?? `Search ${field.label.toLowerCase()}...`
          }
          facetBuckets={options}
          selectedIds={selectedIds}
          showCounts={false}
          compact
          onSearchChange={handleInputChange}
          onChange={(ids) => {
            setChosen(options.filter((b) => ids.includes(b.id)));
            onFormChange(field.param, ids.length > 0 ? ids : undefined);
          }}
        />
        {applyText}
      </div>
    );
  }

  return (
    <div className={style.comboGroup}>
      {field.helperText && (
        <div className={style.fieldLabelRow}>
          <FieldLabel field={field} />
        </div>
      )}
      <TextInput
        id={`combo-search-${field.id}`}
        labelText={field.label}
        hideLabel={!!field.helperText}
        placeholder={
          field.placeholder ?? `Search ${field.label.toLowerCase()}...`
        }
        value={searchText}
        onChange={(e) => handleInputChange(e.target.value)}
      />
      {applyText}
      <PasteListControl
        id={`combo-${field.id}`}
        label={field.label}
        options={facetBuckets}
        selectedIds={selectedIds}
        onApply={(ids) =>
          onFormChange(field.param, ids.length > 0 ? ids : undefined)
        }
        // Every facet behind these filters is capped at 200 buckets, by
        // Query::Wells::Elastic::DEFAULT_FACET_TERMS_SIZES for wells and by
        // the terms aggregations in Query::Documents::Elastic for documents.
        acceptUnmatched={field.pasteableIds}
      />
      {field.selectAll && facetBuckets.length > 0 && (
        <div className={style.selectAllRow}>
          <button
            type="button"
            className={style.selectAllToggle}
            onClick={handleToggleAll}
          >
            {allVisibleSelected ? 'Unselect All' : 'Select All'}
          </button>
          <span className={style.selectAllHint}>Top 200 results shown</span>
        </div>
      )}
      <BucketCheckboxList
        idPrefix={`combo-${field.id}`}
        className={style.comboBuckets}
        buckets={facetBuckets}
        selectedIds={selectedIds}
        emptyHint="No matching values for current filters"
        onToggle={handleCheckboxChange}
      />
    </div>
  );
}

/**
 * The category's typeahead party filters.
 * @param {FilterTemplateProps} props The template props.
 * @return {JSX.Element} The filters.
 */
export function MultiSelectTextSearchComboTemplate({
  category,
  formValues,
  onFormChange,
}: FilterTemplateProps) {
  const facets = useModalFacets();
  const fields = useCategoryFields(category, 'multi-select-text-search-combo');
  const rail = useIsFilterRail();

  const groups = fields.map((field) => (
    <ComboGroup
      key={field.id}
      field={field}
      formValues={formValues}
      facetBuckets={facets?.[field.facetKey]?.buckets ?? []}
      onFormChange={onFormChange}
    />
  ));
  // Laid out one by one, each filter is the form's own field.
  if (rail) return <>{groups}</>;
  return <div className={style.fieldStack}>{groups}</div>;
}
